import { useState } from 'react';
import { TourbookOwner } from '@root/types';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash/fp';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  ConfirmationModal,
  CustomIcon,
  OnlyInDesktopTablet,
  OnlyInMobile,
} from '@components/shared';
import { useHistory } from 'react-router-dom';
import routes from '@root/routes';
import s from './ContactInformation.module.less';

type Props = {
  user: TourbookOwner;
  readOnly: boolean;
};

const ContactInformation = ({ user, readOnly }: Props) => {
  const { t } = useTranslation('tourbook');
  const history = useHistory();
  const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false);
  const flags = useFlags();
  const formattedPhoneNumber = t('common:formattedPhone', {
    formattedPhone: user.tourbookPhoneNumber,
  });

  const handleClickInfoIcon = () => {
    setIsContactInfoModalOpen(true);
  };

  const goToYourAccount = () => {
    history.push(routes.users.edit('tourbook_contact_info'));
  };

  return user ? (
    <div className={s.container}>
      <span className={s.thumb}>
        {user?.tourbookAvatarImage ? (
          <img className={s.avatar} src={user?.tourbookAvatarImage?.smallPath} alt="Avatar" />
        ) : (
          <div className={s.initials} data-testid="initials">
            {user.firstName[0]}
            {user.lastName[0]}
          </div>
        )}
        {user?.tourbookCompanyImage && (
          <img
            className={s.companyLogo}
            src={user?.tourbookCompanyImage?.smallPath}
            alt="Company logo"
          />
        )}
      </span>

      <div>
        <div className={s.contactInformationText}>
          {t('contactInformation.text')}
          {!readOnly && (
            <CustomIcon
              data-testid="contactInfoIcon"
              className={s.infoIcon}
              onClick={handleClickInfoIcon}
              type="info"
            />
          )}
        </div>
        <div className={s.name}>
          {user.firstName} {user.lastName}
        </div>
        {!isEmpty(user.formattedTourbookPhoneNumber) && (
          <>
            <OnlyInMobile>
              <a href={`tel:${user.formattedTourbookPhoneNumber}`} className={s.phoneNumber}>
                {flags['phone-number-format']
                  ? formattedPhoneNumber
                  : user.formattedTourbookPhoneNumber}
              </a>
            </OnlyInMobile>
            <OnlyInDesktopTablet>
              <div className={s.phoneNumber}>
                {flags['phone-number-format']
                  ? formattedPhoneNumber
                  : user.formattedTourbookPhoneNumber}
              </div>
            </OnlyInDesktopTablet>
          </>
        )}
        <a href={`mailto:${user.email}`} className={s.email}>
          {user.email}
        </a>
      </div>

      <ConfirmationModal
        onConfirm={goToYourAccount}
        onClose={() => setIsContactInfoModalOpen(false)}
        title={t('contactInformation.modal.title')}
        confirmationElement={
          <div
            dangerouslySetInnerHTML={{
              __html: t('contactInformation.modal.body', { interpolation: { escapeValue: false } }),
            }}
          />
        }
        confirmButtonText={t('contactInformation.modal.confirmButtonText')}
        isOpen={isContactInfoModalOpen}
        hideCancelButton
      />
    </div>
  ) : null;
};

export default ContactInformation;
